import { Injectable } from '@angular/core';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiDialogsSpinnerComponent } from '@fi-shared/fi-spinner/spinner.component';
import { BehaviorSubject, Subscription } from 'rxjs';
import { IFiSpinnerDialogParams, IFiSpinnerDialogResult } from '@fi-shared/fi-spinner/fi-spinner';
import { takeUntil } from 'rxjs/operators';
import { isDefined } from '@fi-shared/utils';
import { FiSignalRService } from '@fi-shared/fi-signal-r/fi-signal-r.service';
import { getClientGuid } from '@fi-shared/utils/client-guid';
import { ProgressType } from '@fi-shared/fi-signal-r/fi-signal-enums';
import { AppTextService } from '@fi-shared/app-text';

@Injectable()
export class FiSpinnerService {
    constructor(
        private dialogsService: FiDialogsService,
        private fiSignalRService: FiSignalRService,
        private appTextService: AppTextService,
    ) {}

    openSpinnerDialog(params: IFiSpinnerDialogParams): IFiSpinnerDialogResult {
        if (!params.messageText) {
            params.messageText = this.appTextService.get('common.spinner_loading_message');
        }

        const loadingProgress$ = new BehaviorSubject<number>(0);
        const progressSubscriptions: Subscription[] = [];
        if (params.loadingProgress$) {
            progressSubscriptions.push(
                params.loadingProgress$.subscribe((v) => {
                    loadingProgress$.next(v);
                }),
            );
        }
        if (isDefined(params.signalRProgressType)) {
            progressSubscriptions.push(
                this.fiSignalRService.progressEvent.subscribe((data) => {
                    if (data.ClientGuid === getClientGuid() && data.ProgressType === params.signalRProgressType) {
                        loadingProgress$.next(Math.floor(data.Progress * 100));
                    }
                }),
            );
        }

        const spinnerDialogRef = this.dialogsService.custom({
            hideHeader: true,
            hideFooter: true,
            contentComponentRef: FiDialogsSpinnerComponent,
            contentComponentData: {
                headerText: params.headerText,
                messageText: params.messageText,
                loadingProgress$: loadingProgress$.asObservable(),
                isLoadingProgressHidden: params.isLoadingProgressHidden,
            },
        });

        return {
            close: () => {
                progressSubscriptions.forEach((sub) => sub.unsubscribe());
                progressSubscriptions.length = 0;
                loadingProgress$.next(100);
                loadingProgress$.complete();
                spinnerDialogRef.close();
            },
        };
    }

    trackPromise<T>(promise: Promise<T>, params: IFiSpinnerDialogParams): Promise<T> {
        const dialogRef = this.openSpinnerDialog(params);
        promise.finally(() => dialogRef.close());
        return promise;
    }
}
